import React, { useRef, useLayoutEffect, createRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import TightTable from 'components/TightTable';
import Select from 'react-select';
import { Card, CardHeader, CardBody } from 'reactstrap';
import { toPercTable } from 'helpers/formatter';
import PieChart from 'components/PieChart';
import { optionizeAll } from 'helpers/select';
import { toAggregatedDataFrame } from 'helpers/data-frame';
import { useArrayState } from 'components/CustomHooks';

function DetailsPieChart({ chartDataFrame, asset, option }) {
  const [pieChartData, setPieChartData] = useState(null);

  useEffect(() => {
    if (!chartDataFrame) return;

    const weightIndex = chartDataFrame.columns.findIndex((col) => col === 'Weight');
    const weights = chartDataFrame.data.map((row) => row[weightIndex]);
    const _pieChartData = {
      Holdings: toAggregatedDataFrame('Holdings', chartDataFrame.index, weights),
    };

    const allowedColumns = chartDataFrame.columns.filter((v) => !['Weight'].includes(v));
    for (let i = 0; i < allowedColumns.length; i++) {
      const column = allowedColumns[i];
      const columnIndex = chartDataFrame.columns.findIndex((col) => col === column);
      const columnValues = chartDataFrame.data.map((row) => row[columnIndex]);
      const aggregatedData = toAggregatedDataFrame(column, columnValues, weights);
      _pieChartData[column] = aggregatedData;
    }

    setPieChartData(_pieChartData);
  }, [chartDataFrame]);

  if (!pieChartData) return null;

  return (
    <>
      <Card>
        <CardHeader tag="h4" className="mt-0 text-center">
          {asset}
        </CardHeader>
        <CardBody>
          <PieChart
            data={pieChartData[option.value]}
            base="rows"
            field="Weight"
            dataType="percentage"
            containerStyle={{ maxWidth: '400px', maxHeight: '400px' }}
            options={{
              plugins: {
                legend: {
                  display: false,
                },
              },
              layout: {
                padding: {
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                },
              },
            }}
            // containerStyle={{ maxWidth: '500px', maxHeight: '500px' }}
          />
        </CardBody>
      </Card>
      <Card>
        <CardHeader tag="h4" className="mt-0 text-center">
          &nbsp;
        </CardHeader>
        <CardBody>
          <TightTable
            suffix="pba-filtered-details"
            data={pieChartData[option.value]}
            indexName={option.label}
            cellFormatter={(value, row, col) => {
              if (col === 'Weight') return toPercTable(value);
              return value;
            }}
            indexWidth="300px"
            compact
            useSort
          />
        </CardBody>
      </Card>
    </>
  );
}

function DetailsPieCharts({ chartDataFrames, assets }) {
  const [detailsTypeOptions, setDetailsTypeOptions, selectedDetailsTypeOption, setSelectedDetailsTypeOption] =
    useArrayState({
      initialItems: [],
    });

  useEffect(() => {
    if (!chartDataFrames) return;

    const allowedColumns = chartDataFrames[0].columns.filter((v) => !['Symbol', 'Weight'].includes(v));
    const _detailsTypeOptions = optionizeAll(['Holdings', ...allowedColumns]);
    setDetailsTypeOptions(_detailsTypeOptions);
    if (_detailsTypeOptions.length > 0) {
      setSelectedDetailsTypeOption(_detailsTypeOptions[0]);
    }
  }, [chartDataFrames]);

  return (
    <>
      <div className="tw-grid tw-grid-cols-1 tw-content-start tw-items-stretch tw-gap-6 md:tw-grid-cols-6">
        <div className={classnames('tw-max-w-[250px] md:tw-col-span-2 md:tw-col-start-3')}>
          <div className="tw-text-left tw-font-bold">SELECT</div>
          <Select
            className="react-select"
            name="duration"
            value={selectedDetailsTypeOption}
            onChange={setSelectedDetailsTypeOption}
            options={detailsTypeOptions}
          />
        </div>
      </div>
      <div className="tw-grid tw-grid-cols-1 tw-content-start tw-items-stretch tw-gap-6 md:tw-grid-cols-6">
        {chartDataFrames.map((df, index) => {
          return (
            <div
              key={index}
              className={classnames('md:tw-col-span-2', {
                'md:tw-col-start-2': index === 0 && chartDataFrames.length === 2,
                'md:tw-col-start-3': index === 0 && chartDataFrames.length === 1,
              })}
            >
              <DetailsPieChart chartDataFrame={df} asset={assets[index]} option={selectedDetailsTypeOption} />
            </div>
          );
        })}
      </div>
    </>
  );
}

export default DetailsPieCharts;
